/*eslint-disable @typescript-eslint/no-unused-vars*/
/*eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './AddRmsSupportDocuments.css'
import { Id, toast } from 'react-toastify';
import { cloneDeep } from 'lodash';
import axios from 'axios';
import { CustomBreadCrumbs, CustomButtonGroup, DashboardHeader, DashboardLoader, TableSubHeader, useAppDispatch } from '../../../globalUtils/globalExports';
import { fetchPreSignedURLForUpload } from '../../../rmsReduxStore/chargersRedux/chargerCreators';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomForm, { ICustomFormProps, IFormContentProp } from '../../../globalUtils/CustomForm/CustomForm';
import { checkValidAlphaNumericAndDot,  checkValidAlphaNumericDotSpaceAndUnderscore, createBreadCrumbProps, downloadFile, renderHeaderActionButtons } from '../../../globalUtils/globalHooks';
import CustomUpload from '../../../globalUtils/CustomUploadModified/CustomUpload';
import access from '../../../auth/service/AccessControl';
import { createRmsSupportDocument } from '../../../rmsReduxStore/reduxExports';
import { Icon } from '../../../globalUtils/globalIcons';
import CustomChartPopup from '../../softwareReleaseManagement/dashboard/CustomChartPopup';


interface AddRmsSupportDocumentForm {
    id: string | null;
    name: string;
    documentType: IGeneralReferenceData | null;
    version: string;
    description: string;
    uploadedDocument: string;
}

interface IPreSignedUrlInfo {
  blobName: string;
  message: string;
  url: string;
}


const isViewFileAllowedForType = (url: string)=>{
    //Allow only pdfs to be viewed in preview
    const match = url?.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
    return match ? match[1] === 'pdf' : false;
}

const AddRmsSupportDocuments: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const documentData = location?.state?.documentData;
    const initialFormData: AddRmsSupportDocumentForm = {
        id: null,
        name: '',
        documentType: null,
        version: '',
        description: '',
        uploadedDocument: ''
    }
    const [formData,setFormData] = useState<AddRmsSupportDocumentForm>(initialFormData);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [showLoader, setShowLoader] = useState(false);
    const [preSignedUrl, setPreSignedUrl] = useState<IPreSignedUrlInfo | null>(null);
    const [mode,setMode] = useState<string> (location?.state?.mode ? location?.state?.mode : 'add')
    const [showFilePreview,setShowFilePreview] = useState<boolean>(false);

    const formSuccessToast = (message): Id => toast.success(message)
    const formErrorToast = (message): Id => toast.error(message);
    const documentTypeReferenceData = ['Release Notes','Protocol Sheet']?.map(item=>({id:item,name:item,label:item,value:item}));

    const handleFormDataChange = useCallback((value,formKey) => {
        const localFormData = cloneDeep(formData);
        localFormData[formKey] = value;
        setFormData(localFormData);
    },[formData])

    const handleFileUpload = async (signedURL: string, file: File): Promise<void> => {
        try {
        await axios.put(signedURL, file, {
            headers: {
            'Content-Type': file.type,
            'x-ms-blob-type': 'BlockBlob'
            }
        })
        } catch (error) {
        setSelectedFile(null)
        console.log('Error uploading file:', error)
        }
    }

    const handleFileChange = async (file): Promise<void> => {
        if (file) {
          setSelectedFile(file)
          const response = await dispatch(fetchPreSignedURLForUpload({ fileName: file?.name }))
          setPreSignedUrl(response?.data)
          handleFormDataChange(response?.data?.blobName, 'uploadedDocument')
        } else setSelectedFile(null);
    }

    const handleSubmit = useCallback(async () => {
        setShowLoader(true);
        let requestBody: any  = null;
        if(mode === 'add') {
            const addRmsSupportDocumentRequest = {
                name: formData?.name,
                documentType: formData?.documentType?.value,
                version: formData?.version,
                description: formData?.description,
                uploadedDocument: formData?.uploadedDocument
            }

            if (preSignedUrl && selectedFile) {
                handleFileUpload(preSignedUrl?.url, selectedFile)
            }
            requestBody = addRmsSupportDocumentRequest
        }
        else {
            const editRmsSupportDocumentRequest = {
                id: formData?.id,
                name: formData?.name,
                documentType: formData?.documentType?.value,
                version: formData?.version,
                description: formData?.description
            }

            requestBody = editRmsSupportDocumentRequest
        }


        //Todo: replace with actual call
        const response = await dispatch(createRmsSupportDocument(requestBody))

        if( response?.status === 200 || response?.status === 202 ) {
            setShowLoader(false);
            if(mode === 'edit') formSuccessToast('Document edited successfully.')
            else formSuccessToast('Document uploaded successfully.')
            setTimeout(() => {
                navigate('/documentation')
            },2000)
        } else {
            setShowLoader(false);
            formErrorToast(response?.data?.message)
        }

    },[formData])


    const rmsSupportDocumentFormArray: IFormContentProp[] = useMemo(() => [
        {
            rowCountClassName: 'grid-3',
            formObject: [
                {
                    formLabel: 'Document Name',
                    isRequired: true,
                    objectType: 'input',
                    selectDropdownProps: null,
                    inputProps: {
                        inputPlaceHolder: 'Enter Document Name',
                        handleInputChange: (event): void => {
                           if(checkValidAlphaNumericDotSpaceAndUnderscore(event?.target?.value)) handleFormDataChange(event?.target?.value,'name');
                           else formErrorToast('Invalid document name.')
                        },
                        inputValue: formData?.name,
                        inputType: 'string',
                        inputFieldId: 'add_document_name',
                        isRequired: true,
                        autoFocus: true,
                        isDisabled: mode === 'view',
                    },
                    textAreaProps: null,
                },
                {
                    formLabel: 'Version',
                    isRequired: true,
                    objectType: 'input',
                    selectDropdownProps: null,
                    inputProps: {
                        inputPlaceHolder: 'Enter Document Version',
                        handleInputChange: (event): void => {
                            if (checkValidAlphaNumericAndDot(event?.target?.value)) handleFormDataChange(event?.target?.value,'version');
                            else formErrorToast('Invalid document version.')
                        },
                        inputValue: formData?.version,
                        inputType: 'string',
                        inputFieldId: 'add_document_version',
                        isRequired: true,
                        autoFocus: true,
                        isDisabled: mode === 'view',
                    },
                    textAreaProps: null,
                },
                {
                    formLabel: 'Document Type',
                    isRequired: true,
                    objectType: 'dropdown',
                    selectDropdownProps: {
                        selectValue: formData?.documentType,
                        selectOptions: documentTypeReferenceData ?? [],
                        handleSelectOptionChange: (val): void => {
                        handleFormDataChange(val, 'documentType')
                        },
                        selectDropDownId: 'documentType-dropdown',
                        selectDropDownName: 'documentType-dropdown',
                        isDisabled: mode === 'view',
                        renderToBody: true
                    },
                    inputProps: null,
                    textAreaProps: null
                },
            ]
        },
        {
            rowCountClassName: 'grid',
            formObject: [
                {
                    formLabel: 'Description',
                    isRequired: false,
                    objectType: 'text-area',
                    selectDropdownProps: null,
                    inputProps: null,
                    textAreaProps: {
                        inputPlaceHolder: 'Enter Description',
                        handleInputChange: (event): void => {
                        if (event?.target?.value.length <= 1000) {
                            handleFormDataChange(event?.target?.value, 'description')
                        }
                        else formErrorToast('Description can be up to 1000 characters only.')
                        },
                        inputValue: formData?.description,
                        inputFieldId: 'add_document_description',
                        isRequired: true,
                        isDisabled: mode === 'view',
                    },
                }
            ]
        }
    ],[formData,mode,handleFormDataChange])

    const rmsSupportDocumentFormProps: ICustomFormProps = useMemo(() => {
        return {
            formContainerClassName: 'rms__add__document__form',
            formArray: rmsSupportDocumentFormArray
        }
    },[formData,rmsSupportDocumentFormArray])

    const breadCrumbs = createBreadCrumbProps({
        breadCrumbProps:
          [
            {
              objectType: 'link',
              id: 'supportCentre'
            },

            {
              objectType: 'link',
              id: 'documentation'
            },

            {
              objectType: 'text',
              id: 'text',
              text: mode==='view' ? 'View Document' : mode === 'edit'? 'Edit Document' :'Add Document'
            }
    
          ]
    })

    const disableButton = useCallback(() => {
        if(mode === 'view')return true;
        const {name,documentType,version,uploadedDocument} = formData;
        if(!name || name?.trim() === '')return true;
        if(!version || version?.trim() === '')return true;
        if(!documentType?.id) return true;
        if(!uploadedDocument)return true;
        return false;
      }, [formData, selectedFile,mode])

    const btnsList = useMemo(() => {
        return [
          {
            buttonText: mode === 'edit'? 'Update' : 'Add',
            buttonId: 'add',
            btnClassName: disableButton() ? 'primary__btn disabled' : 'primary__btn',
            handleClick: (): void => {
              handleSubmit();
            },
            isDisabled: disableButton(),
            buttonVariant: 'filled'
          },
          {
            buttonText: 'Cancel',
            buttonId: 'cancel',
            btnClassName: 'secondary__btn',
            handleClick: (): void => {
              navigate('/documentation')
            },
            isDisabled: false,
            buttonVariant: 'outlined'
          }
        ]
      }, [formData, selectedFile, mode])

    const headerActionBtns = renderHeaderActionButtons({
        actionBtns:
          [
            {
              id: 'add_version_btn',
              isAccessible: access?.softwareVersionReleases?.addSoftwareVersion?.add(),
              headerActionBtnClick: (): void => {
                navigate('/add-version-release');
              }
            },
          ],
      })

    useEffect(() => {
        if (mode === 'view' || mode === 'edit') {
            setFormData(prev => ({
                ...prev,
                id: documentData?.id,
                name: documentData?.name,
                version: documentData?.version,
                documentType: documentTypeReferenceData?.filter(item=>item?.name === documentData?.documentType)?.[0],
                description: documentData?.description,
                uploadedDocument: documentData?.uploadedDocument
            }))
        }
    },[mode])

    const fileDownloadSuccessToast = useCallback((): Id => {
            return toast.success('File downloaded.');
        }, []);
    
    const fileDownloadFailureToast = useCallback((): Id => {
        return toast.warn('Something went wrong. Please download again.');
        }, []);

    const onDownloadFileIconClick = (url: string) => {
        if(!url) {
            fileDownloadFailureToast()
            return;
        }
        downloadFile({ url });
        fileDownloadSuccessToast()
    }
    
    return (
        <>
            <DashboardLoader showLoader={showLoader} />
            <div className='add__rms__support__documentation__form__container'>
                <CustomBreadCrumbs breadCrumbs={breadCrumbs} />
                <div className='add__rms__support__documentation__form__header-container'>
                    <div className='add__rms__support__documentation__form__header-left'>
                        <DashboardHeader header={mode==='view' ? 'View Document' : mode === 'edit'? 'Edit Document' :'Add Document'}  headerClassName='add__rms__support__documentation__form__header'/>
                        {
                            mode !== 'add' && <Icon iconClassName = {mode === 'view' ? 'rms__pencil__icon' : 'rms__view__icon' } 
                                onIconClick={
                                    () => {
                                        if (mode === 'view') setMode('edit')
                                        else setMode('view')
                                    }
                                }
                            />
                        }
                    </div>
                    <TableSubHeader headerActionBtns={headerActionBtns}/>
                </div>
                <div className='add__rms__support__documentation__form'>
                    <CustomForm {...rmsSupportDocumentFormProps} />
                    <div className='add__rms__support__documentation__form-upload'>
                        {
                          mode === 'add'  ?  <CustomUpload
                            accept='.doc,
                            .docx,
                            application/msword,
                            application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                            .pdf,
                            application/pdf,
                            .xls,
                            .xlsx,
                            application/vnd.ms-excel,
                            application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                            .csv,
                            text/csv,
                            application/csv
                            '
                            className='add__rms__support-document'
                            onFileSelect={(file) => {
                                handleFileChange(file)
                            }}
                            placeholder='Upload Document'
                            isClosable={false}
                        /> :
                        <div className='add__document__form__wrap-content-upload-preview'>
                            <div className='add__document__form__wrap-content-upload-preview-left'>
                                <span className='add__document__uploaded-file-label'>Uploaded Document: </span>
                                <span className={documentData?.uploadedDocument ? 'add__document__uploaded-file-name' : 'add__document__uploaded-file-name-disabled'}>{documentData?.uploadedDocument ? documentData?.name : 'No file found'}</span>
                            </div>
                            <div className='add__document__upload-file-action-btn'>
                                {
                                    isViewFileAllowedForType(documentData?.uploadedDocument) && <div className='add__document__upload-file-action-view-btn' onClick={() => {
                                    if (!documentData?.uploadedDocument) return;
                                    setShowFilePreview(true)
                                }}
                                >
                                    <Icon iconClassName='rms__view__icon' />
                                </div>
                                }
                                <div className='add__document__upload-file-action-download-btn' onClick={()=> {
                                    onDownloadFileIconClick(documentData?.uploadedDocument)
                                }}> 
                                    <Icon iconClassName='rms__documentation__download__icon' />
                                </div>
                            </div>
                        </div>
                        }
                    </div>
                </div>
                <div className='add__rms__support__documentation_form__wrap-footer'>
                        <CustomButtonGroup
                        buttonsList={btnsList}
                        buttonGroupClassName='button__group__footer' />
                </div>
            </div>

            <CustomChartPopup
                show={showFilePreview}
                handleClose={() => { setShowFilePreview(false) }}
            >
                <iframe src={documentData?.uploadedDocument} className='add__documentation__uploaded__file__preview' />
            </CustomChartPopup>
        </>
    )
} 


export default AddRmsSupportDocuments;